import type { ReactElement } from "react";
import type { Unternehmen } from "@kundeninfo/api-client";
import { Chip } from "@kundeninfo/ui/me-design";
import { hausfarbeChip } from "../../markenfarben.js";
import type { Badge } from "../../views/partners/PartnerRoleBadges.js";

/** Ein Kennzeichen (`me-chip` ohne Rahmen, Fläche = Kennzeichen): Text und wahlweise Hausfarbe. */
export type Kennzeichen = { text: string; farbe?: string };

/** Die Folge der Rollen (112): Kunde → Interessent → Lieferant, die Zusatzstufen hinter ihrer Rolle (PF6). */
const ROLLEN_FOLGE: readonly Badge[] = ["CUSTOMER", "PROSPECT", "DORMANT", "FORMER", "SUPPLIER", "SUPPLIER_FORMER"];

/** Rollen nur mit ihrem Text — den gibt der Aufrufer (Partnerliste: Kürzel nach 525a). */
type Rollen = { rollen?: undefined; rollenText?: undefined }
  | { rollen: readonly Badge[]; rollenText: Readonly<Record<Badge, string>> };

/**
 * DIE Folge der Kennzeichen eines Objekts (Entscheide 110, 112; Spec F19): Zugehörigkeit (Unternehmenskürzel in der Folge
 * der Sitzung, mit Hausfarbe; unbekannte Kürzel dahinter) → Rollen → Hinweise in der Folge des Aufrufers. Doppelte nur
 * einmal. Status gehört nicht hierher, er steht rechts.
 */
export function kennzeichenFolge(k: { unternehmen?: readonly string[]; hinweise?: readonly Kennzeichen[] } & Rollen,
  sitzung: readonly Unternehmen[]): Kennzeichen[] {
  const kuerzel = new Set(k.unternehmen ?? []);
  const bekannt = sitzung.map((u) => u.shortCode).filter((c) => kuerzel.has(c));
  const unternehmen = [...bekannt, ...[...kuerzel].filter((c) => !bekannt.includes(c))]
    .map((c) => ({ text: c, ...hausfarbeChip(sitzung, c) }));
  const rollen = k.rollen === undefined ? []
    : ROLLEN_FOLGE.filter((r) => k.rollen.includes(r)).map((r) => ({ text: k.rollenText[r] }));
  return [...unternehmen, ...rollen, ...(k.hinweise ?? [])];
}

/** Die Zugehörigkeit eines Objekts für `kennzeichenFolge`: das Kürzel seines Unternehmens, nur wenn mehrere in der Sicht
 *  stehen (PF20) bzw. es nicht das Arbeitsunternehmen ist (F12) — eine Stelle für Vertrag, Eingangsrechnung, Position. */
export function unternehmenKuerzel(companyId: string, sitzung: readonly Unternehmen[], mehrere: boolean | undefined): string[] {
  const code = mehrere === true ? sitzung.find((u) => u.id === companyId)?.shortCode : undefined;
  return code === undefined ? [] : [code];
}

/** Die Kennzeichen als `Chip`s — ein Fragment ohne Behälter und ohne eigene Maße (PF17); Abstand gibt der Slot. */
export function KennzeichenReihe(p: { liste: readonly Kennzeichen[] }): ReactElement {
  return <>{p.liste.map((k, i) => <Chip key={`${i}:${k.text}`} {...(k.farbe === undefined ? {} : { farbe: k.farbe })}>{k.text}</Chip>)}</>;
}

/** Der Slot `kennzeichen` eines `Objekt`: ohne Kennzeichen `undefined` — kein leerer Träger, keine leere Zeile (PF17). */
export function kennzeichenTeil(liste: readonly Kennzeichen[]): ReactElement | undefined {
  return liste.length === 0 ? undefined : <KennzeichenReihe liste={liste} />;
}
